import { Injectable } from '@angular/core';
import { Keyboard } from '@capacitor/keyboard';
import { BehaviorSubject, Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { isKeyboardTriggerField } from 'src/app/helpers/keyboard-input-scroll';

@Injectable({
  providedIn: 'root'
})
export class KeyboardService {
  private isKeyboardVisible = new BehaviorSubject<boolean>(false);
  private keyboardHeight = new BehaviorSubject<number>(0);

  constructor() {
    this.initializeKeyboardEvents();
  }

  private async initializeKeyboardEvents() {
    if (Capacitor.isNativePlatform()) {
      document.addEventListener('focusin', (event) => {
        const target = event.target as HTMLElement;
        if (isKeyboardTriggerField(target)) {
          this.isKeyboardVisible.next(true);
        }
      });

      document.addEventListener('focusout', (event) => {
        const target = event.target as HTMLElement;
        if (!isKeyboardTriggerField(target)) {
          return;
        }
        setTimeout(() => {
          const active = document.activeElement as HTMLElement | null;
          if (!isKeyboardTriggerField(active)) {
            this.isKeyboardVisible.next(false);
            this.keyboardHeight.next(0);
          }
        }, 0);
      });

      // Écouter les événements du clavier sur les plateformes natives
      Keyboard.addListener('keyboardWillShow', (info) => {
        this.keyboardHeight.next(info.keyboardHeight);
        this.isKeyboardVisible.next(true);
      });

      Keyboard.addListener('keyboardWillHide', () => {
        this.isKeyboardVisible.next(false);
        this.keyboardHeight.next(0);
      });

      Keyboard.addListener('keyboardDidShow', (info) => {
        this.keyboardHeight.next(info.keyboardHeight);
        this.isKeyboardVisible.next(true);
      });

      Keyboard.addListener('keyboardDidHide', () => {
        this.isKeyboardVisible.next(false);
        this.keyboardHeight.next(0);
      });
    } else {
      // Pour le web, utiliser une approche plus robuste
      let initialViewportHeight = window.innerHeight;
      let isKeyboardVisible = false;

      const checkKeyboardState = () => {
        const currentViewportHeight = window.innerHeight;
        const heightDifference = initialViewportHeight - currentViewportHeight;

        // Seuil plus bas pour déclencher plus tôt l'état visible
        const newKeyboardState = heightDifference > 80;
        
        if (newKeyboardState !== isKeyboardVisible) {
          isKeyboardVisible = newKeyboardState;
          this.isKeyboardVisible.next(isKeyboardVisible);
          this.keyboardHeight.next(
            isKeyboardVisible ? Math.max(heightDifference, 0) : 0
          );
        }
      };

      // Écouter les changements de taille de fenêtre
      window.addEventListener('resize', checkKeyboardState);

      document.addEventListener('focusin', (event) => {
        const target = event.target as HTMLElement;
        if (!isKeyboardTriggerField(target)) {
          return;
        }
        if (!isKeyboardVisible) {
          isKeyboardVisible = true;
          this.isKeyboardVisible.next(true);
        }
        setTimeout(checkKeyboardState, 50);
      });

      document.addEventListener('focusout', (event) => {
        const target = event.target as HTMLElement;
        if (!isKeyboardTriggerField(target)) {
          return;
        }
        setTimeout(() => {
          const active = document.activeElement as HTMLElement | null;
          if (isKeyboardTriggerField(active)) {
            return;
          }
          if (isKeyboardVisible) {
            isKeyboardVisible = false;
            this.isKeyboardVisible.next(false);
          }
          checkKeyboardState();
        }, 100);
      });

      // Mettre à jour la hauteur initiale quand l'orientation change
      window.addEventListener('orientationchange', () => {
        setTimeout(() => {
          initialViewportHeight = window.innerHeight;
          checkKeyboardState();
        }, 300);
      });
    }
  }

  /**
   * Retourne un observable qui émet l'état actuel du clavier
   */
  public getKeyboardState(): Observable<boolean> {
    return this.isKeyboardVisible.asObservable();
  }

  /**
   * Retourne l'état actuel du clavier (synchrone)
   */
  public isKeyboardCurrentlyVisible(): boolean {
    return this.isKeyboardVisible.value;
  }

  public getKeyboardHeight(): Observable<number> {
    return this.keyboardHeight.asObservable();
  }

  public getCurrentKeyboardHeight(): number {
    return this.keyboardHeight.value;
  }

  /**
   * Masquer le clavier programmatiquement
   */
  public async hideKeyboard(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      await Keyboard.hide();
    }
  }
} 